import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import { eq } from 'ember-truth-helpers';
import AdaptiveMenu from './adaptive-menu';
import Icon from './icon';
import EllipsisVerticalIcon from '~icons/lucide/ellipsis-vertical';
import FlagIcon from '~icons/lucide/flag';
import ArrowLeftIcon from '~icons/lucide/arrow-left';
import CheckCircleIcon from '~icons/lucide/check-circle-2';
import RefreshCwIcon from '~icons/lucide/refresh-cw';
import type { Store } from '@warp-drive/core';
import type Session from '../services/session';
import type IntlService from 'ember-intl/services/intl';
import type Modals from '../services/modals';
import type Notifications from '../services/notifications';
import {
  createFlag,
  type FlagReason,
  type FlagTargetType,
} from '../builders/flags';
import { deleteUserSourceByContentSourceId } from '../builders/profile';
import { reprocessSource } from '../builders/admin';
import Trash2Icon from '~icons/lucide/trash-2';

interface MoreActionsMenuSignature {
  Args: {
    /** The type of the flaggable entity */
    targetType: FlagTargetType;
    /** The ID of the flaggable entity */
    targetId: string | null;
    /** Current state of the source, used to gate the admin reprocess option */
    sourceState?: string | null;
    /** Whether the source is the user's own upload (sourceType === 'image') — permanently deleted vs. unlinked */
    isUpload?: boolean;
    /** Called after a successful source delete/unlink */
    onDeleted?: () => void | Promise<void>;
    /** Called after a successful admin reprocess request */
    onReprocessed?: () => void | Promise<void>;
  };
}

type Section = 'main' | 'report';

/**
 * Overflow "⋯" menu for source and item detail pages.
 *
 * On mobile it opens as a bottom sheet; on desktop as a dropdown.
 * Uses AdaptiveMenu under the hood.
 *
 * Sections:
 *   - main: list of available actions (Report, …)
 *   - report: inline flag form (no nested menu)
 *
 * Admin options can be added inside the `main` section when needed.
 */
export default class MoreActionsMenu extends Component<MoreActionsMenuSignature> {
  @service declare store: Store;
  @service declare session: Session;
  @service declare intl: IntlService;
  @service declare modals: Modals;
  @service declare notifications: Notifications;

  @tracked section: Section = 'main';
  @tracked selectedReason: FlagReason = 'incorrect';
  @tracked details = '';
  @tracked isSubmitting = false;
  @tracked isDeletingSource = false;
  @tracked isReprocessing = false;
  @tracked submitted = false;
  @tracked errorMessage = '';

  get shouldRender(): boolean {
    return this.session.isAuthenticated;
  }

  get drawerTitle(): string {
    return this.section === 'report' ? 'Report an issue' : 'More actions';
  }

  get canDeleteSource(): boolean {
    return this.args.targetType === 'content_source' && !!this.args.targetId;
  }

  get deleteSourceLabel(): string {
    return this.args.isUpload ? 'Delete source' : 'Remove from my sources';
  }

  get deleteSourceLabelInProgress(): string {
    return this.args.isUpload ? 'Deleting…' : 'Removing…';
  }

  get canReprocess(): boolean {
    return (
      this.session.isAdmin &&
      this.args.targetType === 'content_source' &&
      !!this.args.targetId &&
      this.args.sourceState === 'failed'
    );
  }

  get flagReasons(): { value: FlagReason; label: string }[] {
    return [
      { value: 'incorrect', label: this.intl.t('flags.reason.incorrect') },
      { value: 'spam', label: this.intl.t('flags.reason.spam') },
      {
        value: 'inappropriate',
        label: this.intl.t('flags.reason.inappropriate'),
      },
      { value: 'duplicate', label: this.intl.t('flags.reason.duplicate') },
      { value: 'other', label: this.intl.t('flags.reason.other') },
    ];
  }

  @action
  showReport() {
    this.section = 'report';
  }

  @action
  showMain() {
    this.section = 'main';
  }

  @action
  selectReason(reason: FlagReason) {
    this.selectedReason = reason;
  }

  @action
  updateDetails(event: Event) {
    this.details = (event.target as HTMLTextAreaElement).value;
  }

  @action
  async submitReport(close: () => void) {
    this.isSubmitting = true;
    this.errorMessage = '';
    try {
      const targetId = this.args.targetId;
      if (!targetId) return;
      await this.store.request(
        createFlag({
          targetType: this.args.targetType,
          targetId,
          reason: this.selectedReason,
          details: this.details || undefined,
        })
      );
      this.submitted = true;
      close();
    } catch (error) {
      this.errorMessage =
        error instanceof Error ? error.message : 'Failed to submit report';
    } finally {
      this.isSubmitting = false;
    }
  }

  @action
  async reprocessSource(close: () => void) {
    if (!this.canReprocess || this.isReprocessing) return;

    const confirmed = await this.modals.confirm({
      title: 'Reprocess source',
      message: 'This will re-run extraction on this source from scratch.',
      confirmText: 'Reprocess',
      cancelText: 'Cancel',
    });

    if (!confirmed) return;

    this.isReprocessing = true;
    try {
      const targetId = this.args.targetId;
      if (!targetId) return;

      await this.store.request(reprocessSource(targetId));

      this.notifications.success('Reprocess queued');
      close();
      await this.args.onReprocessed?.();
    } catch (error) {
      this.notifications.error(
        error instanceof Error ? error.message : 'Failed to queue reprocess'
      );
    } finally {
      this.isReprocessing = false;
    }
  }

  @action
  onClose() {
    // Reset to main section (not the form state — keep submitted/reason for UX)
    this.section = 'main';
    this.errorMessage = '';
  }

  @action
  async deleteSource(close: () => void) {
    if (!this.canDeleteSource || this.isDeletingSource) return;

    const confirmed = await this.modals.confirm({
      title: this.args.isUpload ? 'Delete source' : 'Remove from my sources',
      message: this.args.isUpload
        ? 'This permanently deletes your uploaded source and any extracted data not shared elsewhere. This cannot be undone.'
        : 'This removes the source from your sources only — it stays available in the catalog for everyone else.',
      confirmText: this.deleteSourceLabel,
      cancelText: 'Cancel',
      intent: 'error',
    });

    if (!confirmed) return;

    this.isDeletingSource = true;
    try {
      const targetId = this.args.targetId;
      if (!targetId) return;

      const response = await this.store.request(
        deleteUserSourceByContentSourceId(targetId)
      );
      const action = (response as { content?: { data?: { action?: string } } })
        .content?.data?.action;

      if (action === 'deleted') {
        this.notifications.success('Source deleted');
      } else {
        this.notifications.success('Source removed from your list');
      }

      close();
      await this.args.onDeleted?.();
    } catch (error) {
      this.notifications.error(
        error instanceof Error ? error.message : 'Failed to delete source'
      );
    } finally {
      this.isDeletingSource = false;
    }
  }

  <template>
    {{#if this.shouldRender}}
      <AdaptiveMenu
        @placement="dropdown-end"
        @title={{this.drawerTitle}}
        @contentClass="w-72 p-4"
        @onClose={{this.onClose}}
      >
        <:trigger as |toggle|>
          <button
            type="button"
            class="btn btn-ghost btn-sm btn-circle"
            aria-label="More actions"
            aria-haspopup="menu"
            {{on "click" toggle}}
          >
            <Icon @icon={{EllipsisVerticalIcon}} class="h-5 w-5" />
          </button>
        </:trigger>

        <:content as |close|>
          {{#if (eq this.section "main")}}
            {{! Main action list }}
            <ul class="menu w-full p-0">
              {{#if this.submitted}}
                <li>
                  <span class="text-success pointer-events-none gap-2">
                    <Icon @icon={{CheckCircleIcon}} class="h-4 w-4" />
                    Report submitted
                  </span>
                </li>
              {{else}}
                <li>
                  <button
                    type="button"
                    class="gap-2"
                    {{on "click" this.showReport}}
                  >
                    <Icon @icon={{FlagIcon}} class="h-4 w-4 opacity-60" />
                    Report an issue
                  </button>
                </li>
              {{/if}}
              {{#if this.canReprocess}}
                <li>
                  <button
                    type="button"
                    class="gap-2"
                    disabled={{this.isReprocessing}}
                    {{on "click" (fn this.reprocessSource close)}}
                  >
                    <Icon @icon={{RefreshCwIcon}} class="h-4 w-4 opacity-60" />
                    {{if this.isReprocessing "Queuing…" "Reprocess source"}}
                  </button>
                </li>
              {{/if}}
              {{#if this.canDeleteSource}}
                <li>
                  <button
                    type="button"
                    class="text-error gap-2"
                    disabled={{this.isDeletingSource}}
                    {{on "click" (fn this.deleteSource close)}}
                  >
                    <Icon @icon={{Trash2Icon}} class="h-4 w-4 opacity-80" />
                    {{if
                      this.isDeletingSource
                      this.deleteSourceLabelInProgress
                      this.deleteSourceLabel
                    }}
                  </button>
                </li>
              {{/if}}
            </ul>

          {{else}}
            {{! Inline report form — no nested menu }}
            <div>
              <button
                type="button"
                class="btn btn-ghost btn-xs mb-3 -ml-1 gap-1"
                {{on "click" this.showMain}}
              >
                <Icon @icon={{ArrowLeftIcon}} class="h-4 w-4" />
                Back
              </button>

              <h4 class="mb-2 text-sm font-semibold">Report an issue</h4>

              <div class="flex flex-col gap-0.5">
                {{#each this.flagReasons as |r|}}
                  <label class="label cursor-pointer justify-start gap-2 py-1">
                    <input
                      type="radio"
                      name="more-menu-flag-reason"
                      class="radio radio-sm"
                      checked={{eq this.selectedReason r.value}}
                      {{on "change" (fn this.selectReason r.value)}}
                    />
                    <span class="label-text text-sm">{{r.label}}</span>
                  </label>
                {{/each}}
              </div>

              <textarea
                aria-label="Additional details"
                class="textarea textarea-bordered mt-2 w-full text-sm"
                rows="2"
                placeholder="Additional details (optional)"
                {{on "input" this.updateDetails}}
              ></textarea>

              {{#if this.errorMessage}}
                <p class="text-error mt-1 text-xs">{{this.errorMessage}}</p>
              {{/if}}

              <div class="mt-3 flex justify-end gap-2">
                <button
                  type="button"
                  class="btn btn-ghost btn-sm"
                  {{on "click" close}}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="btn btn-error btn-sm"
                  disabled={{this.isSubmitting}}
                  {{on "click" (fn this.submitReport close)}}
                >
                  {{if this.isSubmitting "Sending…" "Submit"}}
                </button>
              </div>
            </div>
          {{/if}}
        </:content>
      </AdaptiveMenu>
    {{/if}}
  </template>
}
